import type { FocusCodeResult } from '@/app/(chat)/api/chat/chat-tools/focus-code-tool';
import type { SetPrFileViewedResult } from '@/app/(chat)/api/chat/chat-tools/set-pr-file-viewed-tool';
import type { ToolProps } from '@/components/ai-elements/tool';
import type { AddPrCommentType } from '@/components/inbox/prs/types/pr-comment-input';
import type { ToolUIPart } from 'ai';
import type { ReactNode } from 'react';

import type { MessagePart } from './message-part-utils';

import { AiAddedPrCommentBlock, AiAddingPrCommentBlock } from './message-blocks/ai-message-block';
import { ToolLoadingIndicator } from './message-blocks/tool-loading-indicator';
import {
  extractGenericToolSummary,
  getGenericToolLoadingMessage,
  getToolDisplayName,
  humanizeToolName,
  isAddPrCommentPart,
  isDynamicToolPart,
  isExaDynamicToolPart,
  isFocusCodePart,
  isGenericStaticToolPart,
  isProposeCustomAgentChangePart,
  isSetPrFileViewedPart,
  isToolOutputComplete,
  TOOL_MARKDOWN_TEXT_CLASS,
} from './message-part-utils';
import { CustomAgentChangeProposalBlock } from './tools/custom-agent-change-proposal-block';
import { ExaCodeContextBlock } from './tools/exa-code-context-block';
import { FocusCodeBlock } from './tools/focus-code-block';
import { pageActionReplayKey } from './tools/page-action-replays';
import { SetPrFileViewedBlock } from './tools/set-pr-file-viewed-block';

import { Tool, ToolContent, ToolHeader } from '@/components/ai-elements/tool';
import { Markdown } from '@/components/chat/markdown';

export type ToolRendererContext = {
  messageId: string;
  handleAiPrComment: (toolCallId?: string) => void;
  getToolProps: (part: MessagePart, index: number) => Pick<ToolProps, 'onOpenChange' | 'open'>;
  toolClassName?: string;
};

type StaticToolRenderer = (
  part: MessagePart,
  index: number,
  context: ToolRendererContext,
) => ReactNode | null;

type InteractiveToolRenderer = {
  title: string;
  loadingMessage: string;
  renderOutput: (part: MessagePart) => ReactNode | null;
};

function getStaticToolName(part: MessagePart): string | null {
  return part.type.startsWith('tool-') ? part.type.slice('tool-'.length) : null;
}

export function renderFocusCodeToolTitle(args: FocusCodeResult): ReactNode {
  const lastSlashIndex = args.filename.lastIndexOf('/');
  const fileName = lastSlashIndex >= 0 ? args.filename.slice(lastSlashIndex + 1) : args.filename;
  const sidePrefix = args.side ? `${args.side}:` : '';

  const sanitizedLineNumbers = Array.from(
    new Set(
      (args.lineNumbers ?? [])
        .map((value) => Math.trunc(value))
        .filter((value) => Number.isFinite(value) && value > 0),
    ),
  ).sort((a, b) => a - b);

  if (sanitizedLineNumbers.length > 1) {
    const firstLine = sanitizedLineNumbers[0];
    const lastLine = sanitizedLineNumbers[sanitizedLineNumbers.length - 1];

    if (
      firstLine !== undefined &&
      lastLine !== undefined &&
      lastLine - firstLine + 1 === sanitizedLineNumbers.length
    ) {
      return `${fileName} ${sidePrefix}${firstLine}-${lastLine}`.trim();
    }

    return `${fileName} ${sidePrefix}${sanitizedLineNumbers.join(',')}`.trim();
  }

  const lineNumber =
    sanitizedLineNumbers[0] ??
    (args.lineNumber && Number.isFinite(args.lineNumber) && args.lineNumber > 0
      ? Math.trunc(args.lineNumber)
      : null);

  return lineNumber ? `${fileName} ${sidePrefix}${lineNumber}`.trim() : fileName;
}

export function renderDynamicToolPart(
  part: MessagePart,
  index: number,
  toolClassName?: string,
  getToolProps?: (part: MessagePart, index: number) => Pick<ToolProps, 'onOpenChange' | 'open'>,
): ReactNode | null {
  if (!isDynamicToolPart(part)) {
    return null;
  }

  if (!isExaDynamicToolPart(part)) {
    const summary = extractGenericToolSummary({
      state: part.state,
      output: part.output,
      errorText: part.errorText,
      identifier: part.toolName,
    });
    const isComplete = isToolOutputComplete(part.state ?? 'output-available');
    const outputNode = isComplete ? (
      summary ? (
        <Markdown className={TOOL_MARKDOWN_TEXT_CLASS}>{summary}</Markdown>
      ) : (
        <span className="text-muted-foreground text-xs">Action completed.</span>
      )
    ) : (
      <ToolLoadingIndicator
        message={part.title ?? getGenericToolLoadingMessage(part.toolName)}
        variant="compact"
      />
    );

    return (
      <Tool
        key={part.toolCallId || `dynamic-tool-${index}`}
        className={toolClassName}
        {...getToolProps?.(part, index)}
      >
        <ToolHeader
          type="dynamic-tool"
          toolName={part.toolName}
          // A code session's steps name what they did (`session-chat-messages.ts`).
          title={part.title ?? humanizeToolName(part.toolName)}
          state={part.state}
        />
        <ToolContent>{outputNode}</ToolContent>
      </Tool>
    );
  }

  const resolvedError =
    part.state === 'output-error' ? part.errorText || 'Unknown error' : undefined;

  const outputNode =
    part.state === 'output-available' || part.state === 'output-error' ? (
      <ExaCodeContextBlock
        query={
          typeof part.input === 'object' &&
          part.input !== null &&
          'query' in part.input &&
          typeof part.input.query === 'string'
            ? part.input.query
            : undefined
        }
        output={part.output}
        errorText={resolvedError}
      />
    ) : (
      <ToolLoadingIndicator message="Looking up documentation..." variant="compact" />
    );

  return (
    <Tool
      key={part.toolCallId || `exa-loading-${index}`}
      className={toolClassName}
      {...getToolProps?.(part, index)}
    >
      <ToolHeader type="dynamic-tool" toolName="get_code_context_exa" state={part.state} />
      <ToolContent>{outputNode}</ToolContent>
    </Tool>
  );
}

// These cards are for app-owned writes from an already-rendered tool result.
// Use AI SDK approvals or addToolOutput when the model needs to continue after
// the user's decision instead of treating Apply/Cancel as a local UI action.
const interactiveToolRenderers: Record<string, InteractiveToolRenderer> = {
  proposeCustomAgentChange: {
    title: 'Custom agent proposal',
    loadingMessage: 'Preparing custom agent proposal...',
    renderOutput: (part) => {
      if (!isProposeCustomAgentChangePart(part)) {
        return null;
      }

      if (part.state === 'output-available' && part.output?.success === true) {
        return <CustomAgentChangeProposalBlock proposal={part.output} />;
      }

      if (part.state === 'output-available' && part.output?.success === false) {
        return <span className="text-muted-foreground text-xs">{part.output.summary}</span>;
      }

      if (part.state === 'output-error') {
        return (
          <span className="text-muted-foreground text-xs">
            {part.errorText ?? 'Failed to prepare custom agent proposal.'}
          </span>
        );
      }

      return null;
    },
  },
};

function renderInteractiveToolPart(
  part: MessagePart,
  index: number,
  context: ToolRendererContext,
): ReactNode | null {
  const toolName = getStaticToolName(part);
  const renderer = toolName ? interactiveToolRenderers[toolName] : undefined;

  if (!renderer) {
    return null;
  }

  const outputNode = renderer.renderOutput(part) ?? (
    <ToolLoadingIndicator message={renderer.loadingMessage} variant="compact" />
  );

  return (
    <Tool
      key={'toolCallId' in part && part.toolCallId ? part.toolCallId : `${toolName}-${index}`}
      className={context.toolClassName}
      {...context.getToolProps(part, index)}
    >
      <ToolHeader
        type={part.type as `tool-${string}`}
        title={renderer.title}
        state={
          ('state' in part && part.state ? part.state : 'output-available') as ToolUIPart['state']
        }
      />
      <ToolContent>{outputNode}</ToolContent>
    </Tool>
  );
}

const staticToolRenderers: StaticToolRenderer[] = [
  (part, index, context) => {
    if (!part.type.startsWith('tool-') || !isAddPrCommentPart(part)) {
      return null;
    }

    const { toolCallId, state, input: args } = part;
    const outputNode =
      state === 'output-available' || state === 'input-available' ? (
        <AiAddedPrCommentBlock
          args={args as AddPrCommentType}
          applyAiComment={() => context.handleAiPrComment(toolCallId)}
        />
      ) : state === 'output-error' ? (
        <span className="text-muted-foreground text-xs">
          {part.errorText ?? 'Failed to add PR comment.'}
        </span>
      ) : (
        <AiAddingPrCommentBlock />
      );

    return (
      <Tool
        key={toolCallId || `loading-${index}`}
        className={context.toolClassName}
        {...context.getToolProps(part, index)}
      >
        <ToolHeader type={part.type} title="Add PR comment" state={part.state} />
        <ToolContent>{outputNode}</ToolContent>
      </Tool>
    );
  },
  renderInteractiveToolPart,
  (part, index, context) => {
    if (!part.type.startsWith('tool-') || !isFocusCodePart(part)) {
      return null;
    }

    const { toolCallId, state, input: args } = part;
    // Try to get result data from multiple possible locations.
    let resultData: FocusCodeResult | undefined;

    // First try the result property (for state === 'output-available').
    if ('output' in part && part.output) {
      resultData = part.output;
    }

    // If no result, try args.
    if (!resultData && args && 'filename' in args) {
      resultData = args as FocusCodeResult;
    }

    const resolvedFocusArgs =
      (state === 'input-available' || state === 'output-available') && resultData?.filename
        ? resultData
        : undefined;

    const outputNode = resolvedFocusArgs ? (
      <FocusCodeBlock args={resolvedFocusArgs} disableAutoNavigate />
    ) : state === 'output-error' ? (
      <span className="text-muted-foreground text-xs">
        {part.errorText ?? 'Failed to focus code.'}
      </span>
    ) : (
      <ToolLoadingIndicator message="Navigating..." variant="compact" />
    );

    return (
      <Tool
        key={toolCallId || `focus-loading-${index}`}
        disableAnimation
        className={context.toolClassName}
        {...context.getToolProps(part, index)}
      >
        <ToolHeader
          type={part.type}
          title={
            resolvedFocusArgs?.filename ? renderFocusCodeToolTitle(resolvedFocusArgs) : 'Focus code'
          }
          state={part.state}
        />
        {resolvedFocusArgs ? (
          <div className="sr-only" aria-hidden="true">
            {/* Keep the navigation side effect mounted even while the visible disclosure stays collapsed. */}
            <FocusCodeBlock
              args={resolvedFocusArgs}
              autoNavigateKey={pageActionReplayKey(context.messageId, toolCallId)}
            />
          </div>
        ) : null}
        <ToolContent>{outputNode}</ToolContent>
      </Tool>
    );
  },
  (part, index, context) => {
    if (!part.type.startsWith('tool-') || !isSetPrFileViewedPart(part)) {
      return null;
    }

    const { toolCallId, state } = part;
    const resultData =
      'output' in part && part.output ? (part.output as SetPrFileViewedResult) : undefined;
    const resolvedArgs = state === 'output-available' && resultData ? resultData : undefined;

    const outputNode = resolvedArgs ? (
      <SetPrFileViewedBlock
        args={resolvedArgs}
        autoApplyKey={pageActionReplayKey(context.messageId, toolCallId)}
      />
    ) : state === 'output-error' ? (
      <span className="text-muted-foreground text-xs">
        {part.errorText ?? 'Failed to update viewed state.'}
      </span>
    ) : (
      <ToolLoadingIndicator message="Updating viewed state..." variant="compact" />
    );

    return (
      <Tool
        key={toolCallId || `set-viewed-loading-${index}`}
        disableAnimation
        className={context.toolClassName}
        {...context.getToolProps(part, index)}
      >
        <ToolHeader
          type={part.type}
          title={
            resolvedArgs ? (resolvedArgs.viewed ? 'Mark viewed' : 'Mark unviewed') : 'Viewed state'
          }
          state={part.state}
        />
        <ToolContent forceMount={resolvedArgs ? true : undefined}>{outputNode}</ToolContent>
      </Tool>
    );
  },
  (part, index, context) => {
    if (!part.type.startsWith('tool-') || !isGenericStaticToolPart(part)) {
      return null;
    }

    const title = getToolDisplayName(part.type);
    const summary = extractGenericToolSummary({
      state: part.state,
      output: part.output,
      errorText: part.errorText,
      identifier: part.type,
    });
    // The think tool returns {} but the reasoning lives in input.thought
    const thinkInput =
      part.type === 'tool-think' &&
      part.state === 'output-available' &&
      'input' in part &&
      typeof part.input === 'object' &&
      part.input !== null
        ? (part.input as Record<string, unknown>)
        : null;
    const effectiveSummary =
      summary ?? (thinkInput && typeof thinkInput.thought === 'string' ? thinkInput.thought : null);
    const isComplete = isToolOutputComplete(part.state ?? 'output-available');
    const outputNode = isComplete ? (
      effectiveSummary ? (
        <Markdown className={TOOL_MARKDOWN_TEXT_CLASS}>{effectiveSummary}</Markdown>
      ) : (
        <span className="text-muted-foreground text-xs">Action completed.</span>
      )
    ) : (
      <ToolLoadingIndicator message={getGenericToolLoadingMessage(part.type)} variant="compact" />
    );

    return (
      <Tool
        key={part.toolCallId || `generic-tool-${index}`}
        className={context.toolClassName}
        {...context.getToolProps(part, index)}
      >
        <ToolHeader
          type={part.type}
          title={title}
          state={(part.state ?? 'output-available') as ToolUIPart['state']}
        />
        <ToolContent>{outputNode}</ToolContent>
      </Tool>
    );
  },
];

export function renderStaticToolPart(
  part: MessagePart,
  index: number,
  context: ToolRendererContext,
): ReactNode | null {
  for (const renderTool of staticToolRenderers) {
    const rendered = renderTool(part, index, context);
    if (rendered) {
      return rendered;
    }
  }

  return null;
}
